import { Component, signal } from '@angular/core';
import { AngularModule } from '../../../../modules/angular.module';
import { Content, LanguageCode, _ } from '@mira-avant/mtlibs';
import { ContentService } from '../../../../services/content.service';
import { NavigationService } from '../../../../services/navigation.service';
import { AdminService } from '../../../../services/admin.service';
import { ZorroModule } from '../../../../modules/zorro.module';
import { LanguageSelectorComponent } from '../../language-selector/language-selector.component';
import { ContentFormComponent } from '../../../forms/standard/content-form/content-form.component';
import { ContentText, EMPTY_CONTENT } from '@mira-avant/mtlibs';
import { v4 } from 'uuid';
import { ClipboardService } from '../../../../services/clipboard.service';
import { Subscription } from 'rxjs';
import { InProgressService } from '../../../../services/in-progress.service';
import { TableFormContainer } from '../table-form-container/table-form-container';
import {
	CmsTable,
	Column,
	ENABLE_COPY_COLUMN,
	ENABLE_DELETE_COLUMN,
	ENABLE_EDIT_COLUMN,
	ENABLE_PREVIEW_COLUMN,
	ENABLE_DISPLAY_ORDER_COLUMN,
	ENABLE_UP_DOWN_COLUMN,
	ENABLE_CHECKBOX_COLUMN,
} from '../cms-table/cms-table';
import { RowColumn } from '../cms-table/row-column';
import { HeaderColumn } from '../cms-table/header-column';
import { AuthService } from '../../../../services/auth.service';
import { ActivityService } from '../../../../services/activity.service';

@Component({
	selector: 'app-content-list',
	standalone: true,
	imports: [
		AngularModule,
		ZorroModule,
		LanguageSelectorComponent,
		ContentFormComponent,
		TableFormContainer,
		CmsTable,
		RowColumn,
		HeaderColumn,
	],
	templateUrl: './content-list.component.html',
	styleUrl: './content-list.component.scss',
})
export class ContentListComponent {
	readonly loading = signal<boolean>(true);
	readonly contents = signal<Content[]>([]);
	readonly uuidsInProgress = signal<string[]>([]);
	readonly editLanguage = signal<string>('en');
	readonly editContent = signal<Content | undefined>(undefined);
	readonly columns = signal<Column[]>([]);
	readonly isAdmin = signal<boolean>(false);
	readonly isFormShowing = signal<boolean>(false);

	private sub: Subscription | undefined;

	constructor(
		public contentService: ContentService,
		private activityService: ActivityService,
		private authService: AuthService,
		public nav: NavigationService,
		private adminService: AdminService,
		public clipboardService: ClipboardService,
		private inProgressService: InProgressService
	) {
		this.sub = this.contentService.contents$.subscribe((contents: Content[]) => {
			this.applyContents(contents);

			this.loading.set(false);
		});

		this.authService.isAdmin$().then((isAdmin: boolean) => {
			this.isAdmin.set(isAdmin);
			this.updateTableDetails();
		});
	}

	private updateTableDetails(): void {
		const columns: Column[] = [];

		if (this.isAdmin()) {
			columns.push(ENABLE_CHECKBOX_COLUMN);
		}

		columns.push(ENABLE_DISPLAY_ORDER_COLUMN, {
			key: 'contentName',
			name: 'Name',
			sortOrder: null,
			sortFn: (a: Content, b: Content) => a.active_text?.name.localeCompare(b.active_text?.name || '') || 0,
			sortDirections: ['ascend', 'descend', null],
			filterMultiple: true,
			filterOptions: this.contents().map(row => ({
				text: row.active_text?.name || '',
				value: row.active_text?.name || '',
			})),
			filterFn: (list: string[], item: Content) => list.some(name => item.active_text?.name.indexOf(name) !== -1),
		});

		if (this.isAdmin()) {
			columns.push(ENABLE_COPY_COLUMN);
			columns.push(ENABLE_UP_DOWN_COLUMN);
			columns.push(ENABLE_EDIT_COLUMN);
			columns.push(ENABLE_DELETE_COLUMN);
		}

		this.columns.set(columns);
	}

	public select($event: unknown): void {
		const content = $event as Content;

		if (!this.adminService.selectedClient$.value) {
			throw new Error('No client selected');
		}

		if (!this.adminService.selectedCourse$.value) {
			throw new Error('No course selected');
		}

		if (!this.adminService.selectedUnit$.value) {
			throw new Error('No unit selected');
		}

		if (!this.adminService.selectedLesson$.value) {
			throw new Error('No lesson selected');
		}

		if (!this.adminService.selectedActivity$.value) {
			throw new Error('No activity selected');
		}

		this.nav.navigate([
			'my',
			'admin',
			'lc',
			{
				clientUuid: this.adminService.selectedClient$.value.uuid,
				courseUuid: this.adminService.selectedCourse$.value.uuid,
				unitUuid: this.adminService.selectedUnit$.value.uuid,
				lessonUuid: this.adminService.selectedLesson$.value.uuid,
				activityUuid: this.adminService.selectedActivity$.value.uuid,
				contentUuid: content.uuid,
			},
		]);
	}

	public copy($event: object): void {
		const content = $event as Content;
		this.clipboardService.copyContent(content);
	}

	public copyBulk($event: object[]): void {
		const contents = $event as Content[];
		this.clipboardService.copyContents(contents);
	}

	public edit($event: unknown): void {
		const content = $event as Content;
		this.editContent.set(this.editContent()?.uuid === content.uuid ? undefined : content);
		this.isFormShowing.set(this.editContent() !== undefined);
	}

	public onShowForm(): void {
		this.isFormShowing.set(true);
	}

	public async delete$($event: unknown): Promise<void> {
		const content = $event as Content;
		this.loading.set(true);
		try {
			await this.contentService.delete$(content.uuid);
			this.contents.set(this.contents().filter(c => c.uuid !== content.uuid));
		} catch (error) {
			console.error('Error deleting content:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public onHideForm(): void {
		this.isFormShowing.set(false);
		this.resetEditState();
	}

	private resetEditState(): void {
		this.editContent.set(undefined);
		this.editLanguage.set(LanguageCode.EN);
	}

	public async moveUp$($event: unknown): Promise<void> {
		const content = $event as Content;

		if (!this.adminService.selectedActivity$.value) {
			throw new Error('No activity selected');
		}
		this.loading.set(true);
		try {
			const updatedContents = await this.contentService.moveUp$(
				content.uuid,
				this.adminService.selectedActivity$.value.uuid
			);
			this.contents.set(updatedContents);
		} catch (error) {
			console.error('Error moving content up:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async moveDown$($event: unknown): Promise<void> {
		const content = $event as Content;

		if (!this.adminService.selectedActivity$.value) {
			throw new Error('No activity selected');
		}
		this.loading.set(true);
		try {
			const updatedContents = await this.contentService.moveDown$(
				content.uuid,
				this.adminService.selectedActivity$.value.uuid
			);
			this.contents.set(updatedContents);
		} catch (error) {
			console.error('Error moving content down:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async onContentFormSubmit$($event: any): Promise<void> {
		let submitSucceeded = false;
		this.loading.set(true);
		try {
			if (this.editContent()) {
				await this.updateContent$($event);
			} else {
				await this.createNewContent$($event);
			}

			await this.refreshActivitiesForSelectedLesson$();
			submitSucceeded = true;
		} catch (error) {
			console.error('Error submitting content form:', error);
		} finally {
			this.loading.set(false);
			this.resetEditState();

			if (submitSucceeded) {
				this.isFormShowing.set(false);
			}
		}
	}

	private async refreshActivitiesForSelectedLesson$(): Promise<void> {
		const selectedLessonUuid = this.adminService.selectedLesson$.value?.uuid;

		if (!selectedLessonUuid) {
			return;
		}

		await this.activityService.getByLesson$(selectedLessonUuid);
	}

	private applyContents(contents: Content[]): void {
		this.contents.set(contents);
		this.updateTableDetails();

		const contentUuids: string[] = contents.map(content => content.uuid);

		this.inProgressService.contentsInProgress$(contentUuids).then((uuids: string[]) => {
			this.uuidsInProgress.set(uuids);
		});

		const currentEditContent = this.editContent();
		if (currentEditContent) {
			const refreshedEditContent = contents.find(content => content.uuid === currentEditContent.uuid);
			this.editContent.set(refreshedEditContent);
		}
	}

	private async createNewContent$($event: any): Promise<void> {
		if (!this.adminService.selectedActivity$.value) {
			throw new Error('No activity selected');
		}

		await this.contentService.create$({
			activity_uuid: this.adminService.selectedActivity$.value.uuid,
			content_type: $event.contentType,
			copied_from_uuid: null,
			default_lang_id: 'en',
			is_instruction: false,
			scoring_method: $event.scoringMethod,
			rubric_level_uuid: $event.rubricLevelUuid,
			skill_code: $event.skillCode || null,
			featured_mode: $event.isFeatured,
			max_score: 0,
			content_text: [
				{
					default_lang_id: 'en',
					name: $event.name,
					data_json: {},
					content_tags: [],
					image_uri: $event.imageUri,
					content_uri: $event.contentUri,
					description_short: $event.descriptionShort,
					description_html: $event.descriptionHtml,
					created: new Date(),
					updated: new Date(),
				},
			],
		});
	}

	private async updateContent$($event: any): Promise<void> {
		const currentEditContent = this.editContent();
		if (!currentEditContent) {
			throw new Error('No content to update');
		}

		const contentTexts: ContentText[] = currentEditContent.content_text;

		let contentText: ContentText | undefined = contentTexts.find(
			text => text.default_lang_id === this.editLanguage()
		);

		if (!contentText) {
			contentText = {
				uuid: v4(),
				content_uuid: currentEditContent.uuid,
				default_lang_id: this.editLanguage(),
				name: $event.name,
				image_uri: $event.imageUri,
				content_uri: $event.contentUri,
				content_tags: [],
				data_json: {},
				description_short: $event.descriptionShort,
				description_html: $event.descriptionHtml,
				created: new Date(),
				updated: new Date(),
			};

			contentTexts.push(contentText);
		} else {
			contentText.name = $event.name;
			contentText.image_uri = $event.imageUri;
			contentText.content_uri = $event.contentUri;
			contentText.data_json = JSON.parse($event.dataJson);
			contentText.description_short = $event.descriptionShort;
			contentText.description_html = $event.descriptionHtml;
			contentText.updated = new Date();
		}

		const props = Object.keys(EMPTY_CONTENT);
		const payload: Content = _.pick(currentEditContent, props) as Content;
		payload.content_type = $event.contentType;
		payload.scoring_method = $event.scoringMethod;
		payload.rubric_level_uuid = $event.rubricLevelUuid;
		payload.skill_code = $event.skillCode || null;
		payload.featured_mode = $event.isFeatured;

		await this.contentService.update$(currentEditContent.uuid, {
			...payload,
			content_text: contentTexts,
		});
	}

	public onEditLanguageChange(code: string): void {
		this.editLanguage.set(code);
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}
}
